import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  updateDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  startAfter
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Subscribe to admin status changes
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAdminStatus = (userId, callback) => {
  const adminDocRef = doc(db, 'adminPortal', userId);
  
  return onSnapshot(adminDocRef, (docSnap) => {
    let isAdmin = false;
    if (docSnap.exists()) {
      const data = docSnap.data();
      isAdmin = data.enabled === true;
    }
    callback(isAdmin);
  }, (error) => {
    console.error('Error in admin status subscription:', error);
    callback(false);
  });
};

// ===== USER MANAGEMENT =====

/**
 * Search user by email
 * @param {string} email - Email to search for
 * @returns {Promise<Object|null>} User object with ID or null if not found
 */
export const searchUserByEmail = async (email) => {
  try {
    if (!email || !email.trim()) {
      return null;
    }

    const normalizedEmail = email.toLowerCase().trim();
    
    // Query Firestore for user with matching email
    const usersCollectionRef = collection(db, 'users');
    const emailQuery = query(usersCollectionRef, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(emailQuery);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first matching user
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error searching user by email:', error);
    throw error;
  }
};

/**
 * Search user by username (case-insensitive)
 * @param {string} username - Username to search for
 * @returns {Promise<Object|null>} User object with ID or null if not found
 */
export const searchUserByUsername = async (username) => {
  try {
    if (!username || !username.trim()) {
      return null;
    }

    const searchTerm = username.trim();
    
    // Get all users and filter by username (case-insensitive)
    // Note: Firestore doesn't support case-insensitive queries natively
    const usersCollectionRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollectionRef);
    
    for (const doc of querySnapshot.docs) {
      const userData = doc.data();
      if (userData.username && userData.username.toLowerCase() === searchTerm.toLowerCase()) {
        return {
          id: doc.id,
          ...userData
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error searching user by username:', error);
    throw error;
  }
};


/**
 * Search user by UID
 * @param {string} uid - User ID to search for
 * @returns {Promise<Object|null>} User object with ID or null if not found
 */
export const searchUserByUID = async (uid) => {
  try {
    if (!uid || !uid.trim()) {
      return null;
    }

    const normalizedUID = uid.trim();
    
    // Get user document directly by ID
    const userDocRef = doc(db, 'users', normalizedUID);
    const userDocSnap = await getDoc(userDocRef);
    
    if (!userDocSnap.exists()) {
      return null;
    }
    
    return {
      id: userDocSnap.id,
      ...userDocSnap.data()
    };
  } catch (error) {
    console.error('Error searching user by UID:', error);
    throw error;
  }
};

/**
 * Get all users
 * @returns {Promise<Array>} Array of user objects with IDs
 */
export const getAllUsers = async () => {
  try {
    const usersCollectionRef = collection(db, 'users');
    const usersQuery = query(usersCollectionRef, orderBy('username'));
    const querySnapshot = await getDocs(usersQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Update user data
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<void>}
 */
export const updateUser = async (userId, userData) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      ...userData,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Subscribe to users collection changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUsers = (callback) => {
  const usersCollectionRef = collection(db, 'users');
  const usersQuery = query(usersCollectionRef, orderBy('username'));
  
  return onSnapshot(usersQuery, (querySnapshot) => {
    const users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(users);
  }, (error) => {
    console.error('Error in users subscription:', error);
    callback([]);
  });
};

/**
 * Fetch paginated users ordered by createdAt descending (newest first)
 * @param {number} pageSize - Number of users per page
 * @param {Object|null} lastDoc - Last document snapshot for cursor-based pagination
 * @returns {Promise<{users: Array, lastDoc: Object|null, hasMore: boolean}>}
 */
export const fetchUsersPaginated = async (pageSize = 20, lastDoc = null) => {
  try {
    const usersCollectionRef = collection(db, 'users');
    let usersQuery;

    if (lastDoc) {
      usersQuery = query(
        usersCollectionRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
    } else {
      usersQuery = query(
        usersCollectionRef,
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(usersQuery);
    const users = querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      users,
      lastDoc: lastVisible,
      hasMore: querySnapshot.docs.length === pageSize
    };
  } catch (error) {
    console.error('Error fetching paginated users:', error);
    throw error;
  }
};

/**
 * Get total user count
 * @returns {Promise<number>}
 */
export const getUserCount = async () => {
  try {
    const usersCollectionRef = collection(db, 'users');
    const snapshot = await getCountFromServer(usersCollectionRef);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting user count:', error);
    throw error;
  }
};

// ===== AVAILABLE QUESTS MANAGEMENT =====

/**
 * Get all available quests
 * @returns {Promise<Array>} Array of quest objects with IDs
 */
export const getAllAvailableQuests = async () => {
  try {
    const questsCollectionRef = collection(db, 'availableQuests');
    const questsQuery = query(questsCollectionRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(questsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching available quests:', error);
    throw error;
  }
};

/**
 * Create new available quest
 * @param {Object} questData - Quest data
 * @returns {Promise<string>} The ID of the newly created quest
 */
export const createAvailableQuest = async (questData) => {
  try {
    // Make sure to add createdAt timestamp and remove id if it exists
    const { id, ...restOfQuestData } = questData;
    const questWithTimestamp = {
      ...restOfQuestData,
      createdAt: Date.now(),
      isActive: false // Quests are inactive by default
    };
    const docRef = await addDoc(collection(db, 'availableQuests'), questWithTimestamp);
    return docRef.id;
  } catch (error) {
    console.error('Error creating available quest:', error);
    throw error;
  }
};

/**
 * Update an existing available quest
 * @param {string} questId - The ID of the quest to update
 * @param {object} questData - The new data for the quest
 * @returns {Promise<void>}
 */
export const updateAvailableQuest = async (questId, questData) => {
  try {
    const questDocRef = doc(db, 'availableQuests', questId);
    const questWithTimestamp = {
      ...questData,
      updatedAt: Date.now()
    };
    await updateDoc(questDocRef, questWithTimestamp);
  } catch (error) {
    console.error('Error updating available quest:', error);
    throw error;
  }
};

/**
 * Delete available quest
 * @param {string} questId - Quest ID
 * @returns {Promise<void>}
 */
export const deleteAvailableQuest = async (questId) => {
  try {
    const questDocRef = doc(db, 'availableQuests', questId);
    await deleteDoc(questDocRef);
  } catch (error) {
    console.error('Error deleting available quest:', error);
    throw error;
  }
};

/**
 * Subscribe to available quests collection changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAvailableQuests = (callback) => {
  const questsCollectionRef = collection(db, 'availableQuests');
  const questsQuery = query(questsCollectionRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(questsQuery, (querySnapshot) => {
    const quests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(quests);
  }, (error) => {
    console.error('Error in available quests subscription:', error);
    callback([]);
  });
};

/**
 * Get active available quests (for regular users)
 * @returns {Promise<Array>} Array of active quest objects
 */
export const getActiveAvailableQuests = async () => {
  try {
    const questsCollectionRef = collection(db, 'availableQuests');
    const questsQuery = query(
      questsCollectionRef, 
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(questsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching active available quests:', error);
    throw error;
  }
};

/**
 * Subscribe to a single available quest by ID
 * @param {string} questId - Quest ID
 * @param {Function} callback - Receives quest object or null
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAvailableQuest = (questId, callback) => {
  if (!questId) {
    return () => {};
  }
  const questDocRef = doc(db, 'availableQuests', questId);
  return onSnapshot(questDocRef, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error in available quest subscription:', error);
    callback(null);
  });
};

// ===== QUEST APPROVAL MANAGEMENT =====

/**
 * Create a new quest request
 * @param {string} userId - User ID who is requesting
 * @param {string} questId - Quest ID from available quests
 * @param {Object} questData - Quest data from available quests
 * @returns {Promise<string>} Generated request ID
 */
export const createQuestRequest = async (userId, questId, questData) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const docRef = await addDoc(requestsCollectionRef, {
      userId,
      questId,
      questData,
      status: 'pending',
      requestedAt: Date.now(),
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating quest request:', error);
    throw error;
  }
};

/**
 * Get all quest requests
 * @returns {Promise<Array>} Array of quest request objects with IDs
 */
export const getAllQuestRequests = async () => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(requestsCollectionRef, orderBy('requestedAt', 'desc'));
    const querySnapshot = await getDocs(requestsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching quest requests:', error);
    throw error;
  }
};

/**
 * Get pending quest requests
 * @returns {Promise<Array>} Array of pending quest request objects
 */
export const getPendingQuestRequests = async () => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching pending quest requests:', error);
    throw error;
  }
};

/**
 * Create an active quest reference for a user from an approved quest request
 * Only stores the available quest's key on the user document.
 * @param {string} userId - User ID
 * @param {string} questId - ID of the quest in availableQuests
 * @returns {Promise<string>} The quest ID stored
 */
export const createActiveQuestFromApprovedRequest = async (userId, questId) => {
  try {
    // Update the user's activeQuest field to only keep a reference to the quest
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      activeQuest: {
        id: questId,
        createdAt: Date.now(),
        updatedAt: Date.now()
      },
      updatedAt: Date.now()
    });
    
    return questId;
  } catch (error) {
    console.error('Error creating active quest from approved request:', error);
    throw error;
  }
};

/**
 * Approve a quest request
 * @param {string} requestId - Request ID
 * @param {string} adminId - Admin ID who is approving
 * @param {string} message - Optional approval message
 * @returns {Promise<void>}
 */
export const approveQuestRequest = async (requestId, adminId, message = '') => {
  try {
    // First, get the quest request to access user ID and quest data
    const requestDocRef = doc(db, 'questRequests', requestId);
    const requestDoc = await getDoc(requestDocRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Quest request not found');
    }
    
    const requestData = requestDoc.data();
    
    // Update the quest request status to approved
    await updateDoc(requestDocRef, {
      status: 'approved',
      approvedBy: adminId,
      approvedAt: Date.now(),
      approvalMessage: message,
      updatedAt: Date.now()
    });
    
    // Create an active quest reference for the user with the approved quest key
    await createActiveQuestFromApprovedRequest(requestData.userId, requestData.questId);
    
  } catch (error) {
    console.error('Error approving quest request:', error);
    throw error;
  }
};

/**
 * Reject a quest request
 * @param {string} requestId - Request ID
 * @param {string} adminId - Admin ID who is rejecting
 * @param {string} message - Optional rejection message
 * @returns {Promise<void>}
 */
export const rejectQuestRequest = async (requestId, adminId, message = '') => {
  try {
    const requestDocRef = doc(db, 'questRequests', requestId);
    await updateDoc(requestDocRef, {
      status: 'rejected',
      rejectedBy: adminId,
      rejectedAt: Date.now(),
      rejectionMessage: message,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error rejecting quest request:', error);
    throw error;
  }
};

/**
 * Subscribe to quest requests changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToQuestRequests = (callback) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(requestsCollectionRef, orderBy('requestedAt', 'desc'));
    
    return onSnapshot(requestsQuery, (querySnapshot) => {
      try {
        console.log('Quest requests snapshot received, docs count:', querySnapshot.docs.length);
        const requests = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Processing request doc:', doc.id, data);
          return {
            id: doc.id,
            ...data
          };
        });
        console.log('Processed quest requests:', requests);
        callback(requests);
      } catch (err) {
        console.error('Error processing quest requests snapshot:', err);
        callback([]);
      }
    }, (error) => {
      console.error('Error in quest requests subscription:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up quest requests subscription:', error);
    // Return a dummy unsubscribe function
    return () => {};
  }
};

/**
 * Subscribe to pending quest requests changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToPendingQuestRequests = (callback) => {
  const requestsCollectionRef = collection(db, 'questRequests');
  const requestsQuery = query(
    requestsCollectionRef, 
    where('status', '==', 'pending'),
    orderBy('requestedAt', 'desc')
  );
  
  return onSnapshot(requestsQuery, (querySnapshot) => {
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(requests);
  }, (error) => {
    console.error('Error in pending quest requests subscription:', error);
    callback([]);
  });
};

/**
 * Subscribe to pending quest requests count only (no doc deserialization).
 * @param {Function} callback - Receives the count (number)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToPendingQuestRequestsCount = (callback) => {
  const requestsQuery = query(
    collection(db, 'questRequests'),
    where('status', '==', 'pending')
  );

  return onSnapshot(requestsQuery, (snapshot) => {
    callback(snapshot.size);
  }, (error) => {
    console.error('Error in pending quest requests count subscription:', error);
    callback(0);
  });
};

/**
 * Get quest requests for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of quest request objects for the user
 */
export const getUserQuestRequests = async (userId) => {
  try {
    const requestsCollectionRef = collection(db, 'questRequests');
    const requestsQuery = query(
      requestsCollectionRef, 
      where('userId', '==', userId),
      orderBy('requestedAt', 'desc')
    );
    const querySnapshot = await getDocs(requestsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user quest requests:', error);
    throw error;
  }
};

/**
 * Subscribe to quest requests for a specific user
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserQuestRequests = (userId, callback) => {
  const requestsCollectionRef = collection(db, 'questRequests');
  const requestsQuery = query(
    requestsCollectionRef, 
    where('userId', '==', userId),
    orderBy('requestedAt', 'desc')
  );
  
  return onSnapshot(requestsQuery, (querySnapshot) => {
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(requests);
  }, (error) => {
    console.error('Error in user quest requests subscription:', error);
    callback([]);
  });
};

// ===== IMAGE UPLOAD MANAGEMENT =====

/**
 * Upload quest icon to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {string} questId - Quest ID (optional, for updates)
 * @returns {Promise<string>} Download URL of uploaded image
 */
export const uploadQuestIcon = async (file, questId = null) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop();
    const fileName = `quest-icons/${timestamp}_${randomString}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, fileName);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading quest icon:', error);
    throw error;
  }
};

/**
 * Delete quest icon from Firebase Storage
 * @param {string} imageUrl - URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteQuestIcon = async (imageUrl) => {
  try {
    if (!imageUrl) return;

    // Extract the file path from the URL
    const url = new URL(imageUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
    
    if (pathMatch) {
      const filePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, filePath);
      await deleteObject(imageRef);
    }
  } catch (error) {
    console.error('Error deleting quest icon:', error);
    // Don't throw error for deletion failures as the image might not exist
  }
}; 